<template>
    <Modal v-if="showModal" :allowClose="true" @modal:close="closeModal" :maxWidth="'max-w-[691px]'" :hideActions="true">
        <template #body>
            <div class="pb-[60px]">
                <div class="text-won-blue">
                    <h2 class="text-center font-semibold text-[24px] leading-[34px]">{{ title }}</h2>
                    <div v-show="!cancelPlan" >
                        <p class="py-3 font-bold text-md leading-[20px] mt-2">
                            You will be losing:
                        </p>

                        <div class="grid grid-cols-2">
                            <subscription-feature
                                v-for="feature of plan.features"
                                :key="plan.id"
                                :feature="feature"
                            />
                        </div>
                    </div>
                </div>
                <form  v-show="cancelPlan" @submit.prevent="cancelSubscription" class="w-full mt-2">
                    <div class="mt-7">
                         <checkbox-element
                          v-for="(checkbox, index) in cancelOptions"
                          :key="index"
                          v-model="reason"
                          :id="checkbox.id"
                          :label="checkbox.label"
                          :value="checkbox.value"
                        />
                        
                        <checkbox-element
                          id="other"
                          v-model="has_feedback"
                          label="Other"
                          value="Other"
                        />

                        <div v-if="showError" class="text-[12px] mt-4 text-red-500">This field is required</div>

                      <div class="flex flex-row justify-between w-full gap-x-3">
                            <Text v-model="user_feedback" :autocomplete="false" placeholder="Other (Please briefly explain)" class="h-[36px] w-full" />
                       </div>
                        <div v-if="has_feedback" class="text-[12px] mt-4 text-red-500">This field is required</div>

                    </div>

                    <div v-if="cancelPlan" class="mt-[19px] flex justify-center">
                         <won-button class="w-full">
                            <svg v-if="submitting" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                 xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                        stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Submit
                        </won-button>
                    </div>
                </form>


                <div v-if="!cancelPlan" class="mt-[19px] flex justify-end">
                    <button @click="closeModal" class="p-[9.5px] w-32 text-[#666666] rounded text-[14px] leading-[17px]">
                        Cancel
                    </button>
    
                    <button @click="handleProceedToCancel" class="p-[9.5px] w-64 bg-won-red-light text-won-red rounded text-[14px] leading-[17px]">
                        Lose Premium Features
                    </button>
                </div>


            </div>
        </template>
    </Modal>
</template>

<script setup>

import Modal from '../../../components/modal'
import Text from "../../../forms/elements/text";
import CheckboxElement from "../../../forms/elements/checkbox.vue";
import { useSubscriptionPlanStore } from "../../../store/subscription.store";
import SubscriptionFeature from "./subscription-feature";
import { useNotifications } from "../../../store/notifications.store";


import { ref } from "vue";
import { cancel } from "../../../api/subscription";
import { setAuthState } from "../../../composables/auth";
import WonButton from "../../won-button.vue";
import {useFeatureUsageStore} from "../../../store/feature-usage.store";
const submitting = ref(false)
const subscriptionPlanStore = useSubscriptionPlanStore()
const auth = setAuthState();
const cancelPlan = ref(false)
const showError = ref(false)
const title = ref('Are you sure you want to cancel your plan?')
const reason = ref([])
const has_feedback = ref(false)
const user_feedback = ref('')
const emit = defineEmits(['cancel-subscription:success'])
const plan = ref(subscriptionPlanStore.fetchPlan(subscriptionPlanStore?.userPlan?.stripe_price))

 const cancelOptions = ref([
      { id: 'got_job', label: 'I got a job!', value: 'I got a job!', checked: false },
      { id: 'expensive', label: 'Premium plan is too expensive', value: 'Premium plan is too expensive', checked: false },
      { id: 'no_longer_need_premium', label: ' No longer need premium features', value: 'No longer need premium features', checked: false },
      { id: 'not_satisfied', label: 'Not satisfied with premium features', value: 'Not satisfied with premium features', checked: false }
 ]);
    
const props = defineProps({
    showModal: {
        type: Boolean,
        default: false,
        required: true
    },
    closeModal: {
        type: Function,
        required: true
    },
    currentPlan: {
        type: String
    }
})

const handleProceedToCancel = () => {
    cancelPlan.value = true
    title.value = 'Sorry to hear that! Please let us know what went wrong so we can improve.  '
}

const cancelSubscription = async () => {
    const notificationStore = useNotifications()
    submitting.value = true
    showError.value = false
    
    try {
        if(reason.value.length === 0 && !has_feedback.value) {
            showError.value = true
            submitting.value = false
            return;
        }
        
         if(user_feedback.value === '' && has_feedback.value) {
            submitting.value = false
            return
        }
        
        if(user_feedback.value != '' && has_feedback.value){
            reason.value.push(user_feedback.value)
        }
        
        const cancelSubscription = await cancel(reason.value)

        if (cancelSubscription?.data?.success) {
            submitting.value = false
            setAuthState().setUser(cancelSubscription.data?.user)
            useSubscriptionPlanStore().setUserPlan(cancelSubscription?.data?.user?.current_plan)
            useSubscriptionPlanStore().setOnGracePeriod(cancelSubscription?.data?.user?.on_grace_period)
            useFeatureUsageStore().setFeatureUsage(cancelSubscription?.data?.user?.feature_usage)
            reason.value = []

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: "subscription_cancelled",
            });

            emit('cancel-subscription:success')
        }

        if (cancelSubscription?.data?.error) {
            submitting.value = false
            notificationStore.notify(cancelSubscription?.data?.error ?? 'Something went wrong', 'error')
        }
        
    } catch(error){
        notificationStore.notify(error.response.data.message ?? 'Something went wrong', 'error')
    }
}

</script>

<style>

[type='checkbox'], [type='radio'] {
    color: #2A8176;
}
</style>

